import React, { useState, memo } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Save, HeartPulse, ShieldAlert, Wheat, Bone, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const METABOLIC_CONDITIONS = [
  { id: 'diabete_type1', fr: 'Diabète Type 1', en: 'Type 1 Diabetes', icon: '💉' },
  { id: 'diabete_type2', fr: 'Diabète Type 2', en: 'Type 2 Diabetes', icon: '📊' },
  { id: 'resistance_insuline', fr: "Résistance à l'insuline", en: 'Insulin Resistance', icon: '🔄' },
  { id: 'hypothyroidie', fr: 'Hypothyroïdie', en: 'Hypothyroidism', icon: '🦋' },
  { id: 'hyperthyroidie', fr: 'Hyperthyroïdie', en: 'Hyperthyroidism', icon: '⚡' },
  { id: 'hypertension', fr: 'Hypertension', en: 'Hypertension', icon: '❤️‍🩹' },
  { id: 'cholesterol_eleve', fr: 'Cholestérol élevé', en: 'High Cholesterol', icon: '🫀' },
  { id: 'syndrome_metabolique', fr: 'Syndrome métabolique', en: 'Metabolic Syndrome', icon: '⚠️' },
  { id: 'apnee_sommeil', fr: 'Apnée du sommeil', en: 'Sleep Apnea', icon: '😴' },
  { id: 'goutte', fr: 'Goutte', en: 'Gout', icon: '🦶' },
];

const ALLERGIES_OPTIONS = [
  { id: 'gluten', fr: 'Gluten', en: 'Gluten', icon: '🌾' },
  { id: 'laitier', fr: 'Produits laitiers', en: 'Dairy', icon: '🥛' },
  { id: 'noix', fr: 'Noix', en: 'Tree Nuts', icon: '🥜' },
  { id: 'arachides', fr: 'Arachides', en: 'Peanuts', icon: '🫘' },
  { id: 'oeufs', fr: 'Œufs', en: 'Eggs', icon: '🥚' },
  { id: 'soja', fr: 'Soja', en: 'Soy', icon: '🫛' },
  { id: 'fruits_de_mer', fr: 'Fruits de mer', en: 'Shellfish', icon: '🦐' },
  { id: 'poisson', fr: 'Poisson', en: 'Fish', icon: '🐟' },
  { id: 'sesame', fr: 'Sésame', en: 'Sesame', icon: '🫓' },
  { id: 'sulfites', fr: 'Sulfites', en: 'Sulfites', icon: '🍷' },
];

const SettingsHealthProfile = memo(function SettingsHealthProfile() {
  const { user, updateUser } = useAuth();
  const { language, t } = useLanguage();
  const hp = user?.health_profile || {};

  const [conditions, setConditions] = useState(hp.metabolic_conditions || []);
  const [allergies, setAllergies] = useState(hp.allergies_intolerances || []);
  const [injuries, setInjuries] = useState(hp.injury_history || []);
  const [newInjury, setNewInjury] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleTag = (list, setList, id) => {
    setList(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const addInjury = () => {
    const trimmed = newInjury.trim();
    if (!trimmed || injuries.includes(trimmed)) return;
    setInjuries(prev => [...prev, trimmed]);
    setNewInjury('');
  };

  const removeInjury = (idx) => {
    setInjuries(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        health_profile: {
          metabolic_conditions: conditions,
          allergies_intolerances: allergies,
          injury_history: injuries,
        }
      };
      await api.put('/users/profile', payload);
      updateUser({ ...user, health_profile: payload.health_profile });
      toast.success(t('settings_subs.health_saved'));
    } catch {
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const TagSelector = ({ options, selected, onToggle, testIdPrefix }) => (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const isActive = selected.includes(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onToggle(opt.id)}
            data-testid={`${testIdPrefix}-${opt.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
              isActive
                ? 'bg-primary/20 border-primary text-primary'
                : 'bg-secondary/50 border-border text-muted-foreground hover:border-primary/40'
            }`}
          >
            <span className="mr-1">{opt.icon}</span>
            {language === 'fr' ? opt.fr : opt.en}
          </button>
        );
      })}
    </div>
  );

  return (
    <Card className="bg-card border-border" data-testid="settings-health-profile">
      <CardHeader>
        <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-red-400" />
          {language === 'fr' ? 'Profil Santé & Blessures' : 'Health Profile & Injuries'}
        </CardTitle>
        <CardDescription>
          {language === 'fr'
            ? 'Ces donnees arment le Coach YUMIX pour adapter ses conseils a ta realite medicale.'
            : 'This data arms Coach YUMIX to adapt advice to your medical reality.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Metabolic Conditions */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-400" />
            {language === 'fr' ? 'Conditions Metaboliques' : 'Metabolic Conditions'}
          </label>
          <TagSelector
            options={METABOLIC_CONDITIONS}
            selected={conditions}
            onToggle={(id) => toggleTag(conditions, setConditions, id)}
            testIdPrefix="condition"
          />
          {conditions.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              {language === 'fr' ? 'Aucune condition sélectionnée' : 'No conditions selected'}
            </p>
          )}
        </div>

        {/* Allergies / Intolerances */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            <Wheat className="w-4 h-4 text-orange-400" />
            {language === 'fr' ? 'Allergies / Intolerances' : 'Allergies / Intolerances'}
          </label>
          <TagSelector
            options={ALLERGIES_OPTIONS}
            selected={allergies}
            onToggle={(id) => toggleTag(allergies, setAllergies, id)}
            testIdPrefix="allergy"
          />
          {allergies.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              {language === 'fr' ? 'Aucune allergie sélectionnée' : 'No allergies selected'}
            </p>
          )}
        </div>

        {/* Injury History */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            <Bone className="w-4 h-4 text-purple-400" />
            {language === 'fr' ? 'Historique de Blessures' : 'Injury History'}
          </label>
          <div className="flex gap-2">
            <Input
              value={newInjury}
              onChange={(e) => setNewInjury(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addInjury()}
              placeholder={language === 'fr' ? 'Ex: Douleur lombaire L4-L5' : 'Ex: Lower back pain L4-L5'}
              className="bg-secondary border-border text-sm"
              data-testid="injury-history-input"
            />
            <Button
              type="button"
              size="sm"
              onClick={addInjury}
              disabled={!newInjury.trim()}
              className="px-3"
              data-testid="injury-history-add-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {injuries.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {injuries.map((injury, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-500/15 border border-purple-500/30 text-purple-300"
                >
                  <span>{injury}</span>
                  <button
                    type="button"
                    onClick={() => removeInjury(idx)}
                    className="hover:text-red-400 transition-colors"
                    data-testid={`injury-remove-${idx}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {injuries.length === 0 && (
            <p className="text-xs text-muted-foreground italic">
              {language === 'fr' ? 'Aucune blessure enregistrée' : 'No injuries recorded'}
            </p>
          )}
        </div>

        {/* Info note */}
        {(conditions.length > 0 || allergies.length > 0 || injuries.length > 0) && (
          <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
            <p className="text-xs text-primary">
              {language === 'fr'
                ? 'Le Coach YUMIX adaptera automatiquement ses recommandations nutritionnelles et sportives en fonction de ces donnees.'
                : 'Coach YUMIX will automatically adapt nutritional and training recommendations based on this data.'}
            </p>
          </div>
        )}

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-primary text-primary-foreground"
          data-testid="health-profile-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {language === 'fr' ? 'Sauvegarder le profil santé' : 'Save health profile'}
        </Button>
      </CardContent>
    </Card>
  );
});

export { METABOLIC_CONDITIONS, ALLERGIES_OPTIONS };
export default SettingsHealthProfile;
